import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginado } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import { Carregando, ErroBox, PageHeader, Paginacao, Vazio } from '../components/ui';

interface Pessoa {
  id: string;
  fullName: string;
  phoneE164: string;
  optOutAt: string | null;
}

interface ConversaResumo {
  id: string;
  eventId: string;
  participantId: string;
  windowExpiresAt: string | null;
  unreadCount: number;
  needsAttention: boolean;
  humanTakeoverBy: string | null;
  lastMessageAt: string | null;
  janelaAberta: boolean;
  janelaRestanteMin: number;
  assumida: boolean;
  participant: {
    id: string;
    jobTitle: string | null;
    company: string | null;
    person: Pessoa;
    event: { id: string; name: string; status: string };
    journey: { id: string; status: string; aiPaused: boolean } | null;
  };
  ultima?: {
    body: string | null;
    direction: 'INBOUND' | 'OUTBOUND';
    author: string;
    status: string;
    createdAt: string;
  } | null;
}

export interface Mensagem {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  author: string;
  status: string;
  body: string | null;
  templateId: string | null;
  sentByUserId: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  failedAt: string | null;
  /**
   * O que o Whisper leu do áudio, ou o extrator leu do documento.
   *
   * Vem de `ParticipantAnswer.transcript`, que a rota de mensagens passou a
   * juntar em 12/10/2026. Antes disso o balão de áudio aparecia como "(mídia
   * ainda não lida)" para sempre: o texto existia no banco e nenhuma tela o lia.
   */
  transcricao: string | null;
  errorMessage: string | null;
  createdAt: string;
}

interface Resumo {
  precisamAtencao: number;
  naoLidas: number;
  janelaAberta: number;
  total: number;
}

/**
 * O ícone de status do WhatsApp, com o mesmo significado que a pessoa vê do
 * outro lado. Falha ganha cor: é a única que pede ação de quem atende.
 */
const STATUS: Record<string, { icone: string; titulo: string; cor?: string }> = {
  PENDENTE: { icone: 'ti-clock', titulo: 'Na fila' },
  ENVIADA: { icone: 'ti-check', titulo: 'Enviada' },
  ENTREGUE: { icone: 'ti-checks', titulo: 'Entregue' },
  LIDA: { icone: 'ti-checks', titulo: 'Lida', cor: 'var(--whatsapp)' },
  FALHA: { icone: 'ti-alert-circle', titulo: 'Falhou', cor: 'var(--danger)' },
};

const FILTROS = [
  { valor: '', rotulo: 'Todas' },
  { valor: 'atencao', rotulo: 'Precisam de atenção' },
  { valor: 'naoLidas', rotulo: 'Não lidas' },
  { valor: 'aberta', rotulo: 'Janela aberta' },
] as const;

/**
 * WhatsApp — a caixa de entrada de quem atende.
 *
 * A tela de quem ATENDE — organizada por fila, não por evento. Três coisas
 * precisam estar visíveis o tempo todo, e cada uma existe por um motivo:
 *
 * 1. **A janela de 24 horas.** Fora dela o WhatsApp só entrega template
 *    aprovado. Sem o aviso, o operador escreve, envia e a mensagem não sai.
 * 2. **De qual evento é a conversa.** Quando a mesma pessoa está em dois
 *    eventos do mesmo cliente, o sistema escolhe por heurística e pode errar —
 *    quem atende é o único que pode perceber.
 * 3. **Quem está conduzindo**, humano ou automação. Duas vozes na mesma
 *    conversa é o pior resultado possível para quem está do outro lado.
 */
export function Conversas() {
  const cliente = useQueryClient();
  const { pode, usuario } = useAuth();

  const [filtro, setFiltro] = useState<string>('');
  const [pagina, setPagina] = useState(1);
  const [abertaId, setAbertaId] = useState<string | null>(null);

  const parametros = new URLSearchParams({ page: String(pagina) });
  if (filtro === 'atencao') parametros.set('precisaAtencao', 'true');
  if (filtro === 'naoLidas') parametros.set('naoLidas', 'true');
  if (filtro === 'aberta') parametros.set('janela', 'aberta');

  const lista = useQuery({
    queryKey: ['conversas', filtro, pagina],
    queryFn: () => api<Paginado<ConversaResumo>>(`/conversations?${parametros}`),
    // A conversa acontece em tempo real do outro lado. Sem recarregar sozinha,
    // a tela mostra uma fila que já mudou — e o operador só descobre ao clicar.
    refetchInterval: 15_000,
  });

  const resumo = useQuery({
    queryKey: ['conversas', 'resumo'],
    queryFn: () => api<Resumo>('/conversations/resumo'),
    refetchInterval: 30_000,
  });

  const conversas = lista.data?.data ?? [];
  const aberta = conversas.find((c) => c.id === abertaId) ?? null;

  return (
    <>
      <PageHeader
        titulo="WhatsApp"
        subtitulo="Cada conversa é com um participante. Responder aqui pausa a automação."
        acoes={
          <div style={{ display: 'flex', gap: 6 }}>
            {FILTROS.map((f) => (
              <button
                key={f.valor}
                className={`btn btn-sm ${filtro === f.valor ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => {
                  setFiltro(f.valor);
                  setPagina(1);
                }}
              >
                {f.rotulo}
                {f.valor === 'atencao' && resumo.data?.precisamAtencao
                  ? ` (${resumo.data.precisamAtencao})`
                  : ''}
              </button>
            ))}
          </div>
        }
      />

      {lista.isPending && <Carregando />}
      {lista.isError && <ErroBox erro={lista.error} aoTentar={() => lista.refetch()} />}

      {lista.data &&
        (conversas.length === 0 ? (
          <Vazio
            icone="ti-message-circle"
            titulo={filtro ? 'Nada nesse filtro' : 'Nenhuma conversa ainda'}
            descricao={
              filtro
                ? undefined
                : 'A conversa nasce quando o convite de uma jornada sai. Inicie uma jornada em um evento para que a primeira apareça aqui.'
            }
          />
        ) : (
          <div className="central">
            <div className="card central-coluna" style={{ padding: 0 }}>
              <div className="conversa-fila">
                {conversas.map((c) => (
                  <ItemDaFila
                    key={c.id}
                    conversa={c}
                    ativa={c.id === abertaId}
                    aoAbrir={() => setAbertaId(c.id)}
                  />
                ))}
              </div>
              <div style={{ padding: 10, borderTop: '1px solid var(--border)' }}>
                <Paginacao meta={lista.data.meta} aoMudar={setPagina} />
              </div>
            </div>

            {aberta ? (
              <Conversa
                conversa={aberta}
                usuarioId={usuario?.id}
                podeEnviar={pode('conversations.send')}
                podeAssumir={pode('conversations.takeover')}
                aoMudar={() => {
                  cliente.invalidateQueries({ queryKey: ['conversas'] });
                }}
              />
            ) : (
              <div className="card central-coluna" style={{ justifyContent: 'center' }}>
                <Vazio
                  icone="ti-message-2"
                  titulo="Escolha uma conversa"
                  descricao="A fila à esquerda está ordenada pela última mensagem — a mais recente primeiro."
                />
              </div>
            )}
          </div>
        ))}
    </>
  );
}

/* ─────────────── A fila ─────────────── */

function ItemDaFila({
  conversa,
  ativa,
  aoAbrir,
}: {
  conversa: ConversaResumo;
  ativa: boolean;
  aoAbrir: () => void;
}) {
  const pessoa = conversa.participant.person;

  return (
    <button className={`conversa-item ${ativa ? 'ativa' : ''}`} onClick={aoAbrir}>
      <div className="conversa-topo-linha">
        <span className="celula-principal" style={{ fontSize: 13 }}>
          {pessoa.fullName}
        </span>
        {conversa.unreadCount > 0 && (
          <span className="contador-nao-lidas">{conversa.unreadCount}</span>
        )}
      </div>

      {/* De qual evento é a conversa — nunca some da lista. */}
      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
        {conversa.participant.event.name}
      </div>

      <div className="conversa-previa">
        {conversa.ultima
          ? `${conversa.ultima.direction === 'OUTBOUND' ? 'Você: ' : ''}${
              conversa.ultima.body ?? '(mídia)'
            }`
          : 'Sem mensagens ainda'}
      </div>

      <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
        {conversa.needsAttention && <Tag type="warning">Precisa de atenção</Tag>}
        {pessoa.optOutAt ? (
          <Tag type="danger">Saiu da lista</Tag>
        ) : conversa.janelaAberta ? (
          <Tag type="success">{formatarJanela(conversa.janelaRestanteMin)}</Tag>
        ) : (
          <Tag type="muted">Janela fechada</Tag>
        )}
        {conversa.assumida && <Tag type="info">Assumida</Tag>}
      </div>
    </button>
  );
}

/* ─────────────── A conversa aberta ─────────────── */

function Conversa({
  conversa,
  usuarioId,
  podeEnviar,
  podeAssumir,
  aoMudar,
}: {
  conversa: ConversaResumo;
  usuarioId?: string;
  podeEnviar: boolean;
  podeAssumir: boolean;
  aoMudar: () => void;
}) {
  const cliente = useQueryClient();
  const [texto, setTexto] = useState('');
  const fim = useRef<HTMLDivElement>(null);
  const pessoa = conversa.participant.person;

  const historico = useQuery({
    queryKey: ['conversa', conversa.id, 'mensagens'],
    queryFn: () => api<{ data: Mensagem[]; temMais: boolean }>(
      `/conversations/${conversa.id}/mensagens`,
    ),
    refetchInterval: 10_000,
  });

  const invalidar = () => {
    cliente.invalidateQueries({ queryKey: ['conversa', conversa.id] });
    aoMudar();
  };

  const responder = useMutation({
    mutationFn: (corpo: string) =>
      api(`/conversations/${conversa.id}/responder`, {
        method: 'POST',
        body: { texto: corpo },
      }),
    onSuccess: () => {
      setTexto('');
      invalidar();
    },
  });

  const takeover = useMutation({
    mutationFn: (qual: 'assumir' | 'devolver') =>
      api(`/conversations/${conversa.id}/${qual}`, { method: 'POST' }),
    onSuccess: invalidar,
  });

  const marcarLida = useMutation({
    mutationFn: () => api(`/conversations/${conversa.id}/lida`, { method: 'POST' }),
    onSuccess: aoMudar,
  });

  // Abrir a conversa é lê-la. Sem isto o contador ficaria para sempre.
  useEffect(() => {
    if (conversa.unreadCount > 0) marcarLida.mutate();
    // Só quando a conversa muda: `marcarLida` recria a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversa.id]);

  // Rola para o fim quando chegam mensagens novas.
  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'end' });
  }, [historico.data?.data.length]);

  const bloqueio = pessoa.optOutAt
    ? 'Esta pessoa pediu para não receber mais mensagens. Reativar exige consentimento novo.'
    : !conversa.janelaAberta
      ? 'A janela de 24 horas fechou. Fora dela o WhatsApp só entrega template aprovado — a resposta livre volta a valer assim que a pessoa escrever de novo.'
      : null;

  return (
    <div className="card central-coluna" style={{ padding: 0 }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div className="celula-principal">{pessoa.fullName}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {[conversa.participant.jobTitle, conversa.participant.company]
                .filter(Boolean)
                .join(' · ') || pessoa.phoneE164}
            </div>
            {/* O evento é link: quando a heurística do webhook erra, o caminho
                para conferir precisa estar a um clique. */}
            <div style={{ fontSize: 12, marginTop: 4 }}>
              <i className="ti ti-calendar-star" style={{ fontSize: 12 }} />{' '}
              <Link to={`/eventos/${conversa.participant.event.id}`}>
                {conversa.participant.event.name}
              </Link>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            {podeAssumir &&
              (conversa.assumida ? (
                <button
                  className="btn btn-secondary btn-sm"
                  title="A automação volta a responder esta conversa"
                  disabled={takeover.isPending}
                  onClick={() => takeover.mutate('devolver')}
                >
                  <i className="ti ti-robot" /> Devolver à automação
                </button>
              ) : (
                <button
                  className="btn btn-secondary btn-sm"
                  title="A automação para de responder esta conversa"
                  disabled={takeover.isPending}
                  onClick={() => takeover.mutate('assumir')}
                >
                  <i className="ti ti-hand-stop" /> Assumir
                </button>
              ))}
          </div>
        </div>

        {conversa.assumida && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            <i className="ti ti-hand-stop" />{' '}
            {conversa.humanTakeoverBy === usuarioId
              ? 'Você assumiu esta conversa — a automação está parada nela.'
              : 'Outra pessoa assumiu esta conversa. A automação está parada nela.'}
          </div>
        )}
      </div>

      <div className="thread">
        {historico.isPending && <Carregando />}
        {historico.data?.temMais && (
          <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-dim)' }}>
            Mostrando as mensagens mais recentes.
          </div>
        )}
        {historico.data?.data.map((m) => (
          <Balao key={m.id} mensagem={m} />
        ))}
        <div ref={fim} />
      </div>

      {bloqueio ? (
        <div className="aviso" style={{ margin: 11 }}>
          <i className="ti ti-clock-x" />
          <span>{bloqueio}</span>
        </div>
      ) : (
        podeEnviar && (
          <form
            className="compositor"
            onSubmit={(e) => {
              e.preventDefault();
              if (texto.trim()) responder.mutate(texto.trim());
            }}
          >
            <textarea
              className="form-input"
              rows={2}
              maxLength={4096}
              placeholder="Escreva uma resposta…"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                // Enter envia, Shift+Enter quebra linha — como no WhatsApp.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (texto.trim()) responder.mutate(texto.trim());
                }
              }}
            />
            <button
              className="btn btn-primary"
              type="submit"
              disabled={responder.isPending || !texto.trim()}
            >
              <i className="ti ti-send" /> {responder.isPending ? 'Enviando…' : 'Enviar'}
            </button>
          </form>
        )
      )}

      {responder.isError && (
        <div style={{ padding: '0 11px 11px' }}>
          <ErroBox erro={responder.error} />
        </div>
      )}
    </div>
  );
}

/**
 * Uma mensagem, como a pessoa a viu do outro lado.
 *
 * Exportado porque a aba "Jornada" da tela de perfil mostra a mesma conversa, e
 * duas versões do balão divergiriam — uma ganharia o status de entrega e a
 * outra não, e aí a mesma conversa contaria duas histórias dependendo de onde
 * se olha.
 */
export function Balao({ mensagem }: { mensagem: Mensagem }) {
  const saiu = mensagem.direction === 'OUTBOUND';
  const status = STATUS[mensagem.status];
  const quando = new Date(mensagem.sentAt ?? mensagem.createdAt);

  return (
    <div className={`balao ${saiu ? 'saida' : 'entrada'}`}>
      {mensagem.body}

      {/*
        A transcrição do áudio, marcada como transcrição.

        ⚠️ **Marcada, e não mostrada como se fosse o texto da mensagem.** Quem lê
        precisa saber que aquilo saiu de um modelo: foi lendo uma transcrição que
        apareceram os nomes próprios errados em 11/10/2026, e um texto que se
        apresenta como "o que a pessoa escreveu" não convida ninguém a
        desconfiar dele.

        Antes disto, este balão mostrava "(mídia ainda não lida)" para sempre —
        o texto existia em `ParticipantAnswer.transcript` e nenhuma tela o lia.
      */}
      {mensagem.transcricao && (
        <div className={mensagem.body ? 'balao-transcricao com-corpo' : 'balao-transcricao'}>
          <span className="balao-transcricao-rotulo">
            <i className="ti ti-waveform" /> transcrito do áudio
          </span>
          {mensagem.transcricao}
        </div>
      )}

      {!mensagem.body && !mensagem.transcricao && (
        <em style={{ color: 'var(--text-muted)' }}>(mídia ainda não lida)</em>
      )}

      <div className="balao-rodape">
        {/* Quem falou: a distinção entre humano e automação é o que permite
            entender depois por que a conversa foi por um caminho. */}
        {saiu && mensagem.author === 'HUMANO' && <span>equipe</span>}
        {saiu && mensagem.author === 'IA' && <span>automático</span>}
        {saiu && mensagem.templateId && <span title="Enviada por template aprovado">template</span>}

        <span>
          {quando.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>

        {saiu && status && (
          <i className={`ti ${status.icone}`} title={status.titulo} style={{ color: status.cor }} />
        )}
      </div>

      {mensagem.errorMessage && (
        <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 3 }}>
          {mensagem.errorMessage}
        </div>
      )}
    </div>
  );
}

/** "faltam 3h" diz mais que um horário de expiração. */
function formatarJanela(minutos: number) {
  if (minutos >= 120) return `${Math.floor(minutos / 60)}h de janela`;
  if (minutos >= 60) return '1h de janela';
  return `${minutos} min de janela`;
}
